import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { hub } from '@/lib/hub-client';

/**
 * "Remove user from track" (HUB-109 D7) — ONE sequence shared by the track
 * page's Access tab and the user page's Tracks tab. Every held level relation
 * is deleted one by one, then every role membership in one call, so nothing is
 * left behind. Relation strings never reach the screen: they are read from the
 * users list and handed straight back to the hub.
 */

/** The direct track levels, widest first — the only relations a level grant uses. */
export const TRACK_LEVELS = ['admin', 'member', 'viewer'] as const;
const LEVEL_SET = new Set<string>(TRACK_LEVELS);

export const LAST_ADMIN_TEXT = 'Add another admin first.';

export interface TrackAccessUser {
  userId: string;
  relations: string[];
  roles: { id: string; name: string }[];
}

/** The level relations a user holds, in the order the users list returned them. */
export function levelRelations(relations: readonly string[]): string[] {
  return relations.filter((relation) => LEVEL_SET.has(relation));
}

const trackParams = (trackId: string) => ({ objectType: 'track', objectId: trackId });

export async function deleteLevelRelations(trackId: string, userId: string, relations: readonly string[]): Promise<void> {
  for (const relation of levelRelations(relations)) {
    await hub.objectsRelationDelete({
      params: trackParams(trackId),
      body: { relation, subjectType: 'user', subjectId: userId },
    });
  }
}

export async function deleteRoleMemberships(trackId: string, userId: string, roleIds: readonly string[]): Promise<void> {
  if (roleIds.length === 0) return;
  await hub.objectsRelationDelete({
    params: trackParams(trackId),
    body: { roleIds: [...roleIds], subjectType: 'user', subjectId: userId },
  });
}

export async function removeUserFromTrack(trackId: string, user: TrackAccessUser): Promise<void> {
  await deleteLevelRelations(trackId, user.userId, user.relations);
  await deleteRoleMemberships(trackId, user.userId, user.roles.map((role) => role.id));
}

/** The hub's 409 `last_admin` becomes the one sentence the operator can act on. */
export function accessErrorMessage(err: unknown): string {
  if (err instanceof HubClientError && err.code === 'last_admin') return LAST_ADMIN_TEXT;
  return (err as Error).message ?? 'Request failed';
}
